// @ts-strict-ignore
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionIcon from "@material-ui/icons/Description";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ImageIcon from "@material-ui/icons/Image";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PhoneIcon from "@material-ui/icons/Phone";
import VideocamIcon from "@material-ui/icons/Videocam";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { WhatsAppTemplate } from "../types";

interface TemplatePreviewDialogProps {
  open: boolean;
  template: WhatsAppTemplate | null;
  onClose: () => void;
}

export const TemplatePreviewDialog: React.FC<TemplatePreviewDialogProps> = ({
  open,
  template,
  onClose,
}) => {
  if (!template) return null;

  const headerFmt = (template.headerFormat || "").toUpperCase();
  const buttons = template.buttons || [];

  // Parse parameter schema to show list of expected parameters
  let schemaMap: Record<string, string> = {};
  if (template.parameterSchema) {
    try {
      schemaMap =
        typeof template.parameterSchema === "string"
          ? JSON.parse(template.parameterSchema)
          : template.parameterSchema;
    } catch {
      schemaMap = {};
    }
  }

  // Render body text with highlighted parameter chips
  const renderFormattedBody = (rawText: string) => {
    if (!rawText) {
      return (
        <Typography variant="body2" style={{ fontStyle: "italic", color: "#64748b" }}>
          (No body text configured for this template)
        </Typography>
      );
    }

    // Split by {{N}} patterns and highlight variables
    const parts = rawText.split(/(\{\{\d+\}\})/g);
    return (
      <Typography
        variant="body2"
        style={{
          color: "#111827",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {parts.map((part, index) => {
          const match = part.match(/^\{\{(\d+)\}\}$/);
          if (match) {
            const varNum = match[1];
            return (
              <span
                key={index}
                style={{
                  backgroundColor: "#dbeafe",
                  color: "#1e40af",
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: 4,
                  border: "1px solid #bfdbfe",
                  fontSize: "0.85rem",
                  margin: "0 2px",
                  display: "inline-block",
                }}
              >
                {`{{${varNum}}}`}
              </span>
            );
          }
          return part;
        })}
      </Typography>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              WhatsApp Template Preview
            </Typography>
            <Chip
              size="small"
              label={template.status}
              style={{
                backgroundColor: template.status === "APPROVED" ? "#dcfce7" : "#fee2e2",
                color: template.status === "APPROVED" ? "#15803d" : "#b91c1c",
                fontWeight: 700,
              }}
            />
          </Box>
          <IconButton size="small" onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ backgroundColor: "#f1f5f9", padding: "20px 24px" }}>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Simulated WhatsApp Chat View */}
          <Box
            style={{
              backgroundColor: "#e5ddd5",
              backgroundImage: "radial-gradient(#d1c7bc 1px, transparent 1px)",
              backgroundSize: "16px 16px",
              borderRadius: 16,
              padding: "24px 16px",
              display: "flex",
              justifyContent: "center",
              boxShadow: "inset 0 2px 4px rgba(0,0,0,0.06)",
            }}
          >
            {/* WhatsApp Incoming Chat Bubble */}
            <Box
              style={{
                maxWidth: 420,
                width: "100%",
                backgroundColor: "#ffffff",
                borderRadius: "12px 12px 12px 4px",
                overflow: "hidden",
                boxShadow: "0 2px 5px rgba(0,0,0,0.12)",
                border: "1px solid rgba(0,0,0,0.05)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header Media Area */}
              {headerFmt === "IMAGE" && (
                <Box
                  style={{
                    backgroundColor: "#f3f4f6",
                    height: 180,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom: "1px solid #e5e7eb",
                    color: "#6b7280",
                    gap: 6,
                  }}
                >
                  <ImageIcon style={{ fontSize: 44, color: "#9ca3af" }} />
                  <Typography variant="caption" style={{ fontWeight: 600 }}>
                    Header Image (from Media Library)
                  </Typography>
                </Box>
              )}

              {headerFmt === "VIDEO" && (
                <Box
                  style={{
                    backgroundColor: "#1f2937",
                    height: 180,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom: "1px solid #374151",
                    color: "#e5e7eb",
                    gap: 6,
                  }}
                >
                  <VideocamIcon style={{ fontSize: 44, color: "#38bdf8" }} />
                  <Typography variant="caption" style={{ fontWeight: 600 }}>
                    Header Video (from Media Library)
                  </Typography>
                </Box>
              )}

              {headerFmt === "DOCUMENT" && (
                <Box
                  style={{
                    margin: "12px 12px 4px",
                    padding: 12,
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <DescriptionIcon style={{ fontSize: 32, color: "#16a34a" }} />
                  <Box display="flex" flexDirection="column">
                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#166534" }}>
                      PDF Attachment
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Header Document (from Media Library)
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Message Body Content */}
              <Box padding={2} style={{ paddingBottom: 10 }}>
                {renderFormattedBody(template.bodyText || "")}

                {/* Simulated timestamp and delivery status */}
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={0.5}
                  marginTop={1}
                >
                  <Typography variant="caption" style={{ color: "#9ca3af", fontSize: "0.75rem" }}>
                    10:42 AM
                  </Typography>
                  <CheckIcon style={{ fontSize: 13, color: "#3b82f6" }} />
                </Box>
              </Box>

              {/* Interactive Buttons (WhatsApp Style) */}
              {buttons.length > 0 && (
                <Box
                  style={{
                    borderTop: "1px solid #f1f5f9",
                    backgroundColor: "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {buttons.map((btnType, idx) => (
                    <Box
                      key={idx}
                      style={{
                        padding: "10px 14px",
                        borderTop: idx > 0 ? "1px solid #f1f5f9" : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        color: "#0284c7",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                      }}
                    >
                      {btnType === "URL" && <OpenInNewIcon style={{ fontSize: 16 }} />}
                      {btnType === "COPY_CODE" && <FileCopyIcon style={{ fontSize: 16 }} />}
                      {btnType === "PHONE_NUMBER" && <PhoneIcon style={{ fontSize: 16 }} />}
                      <span>
                        {btnType === "URL"
                          ? "Visit Website"
                          : btnType === "COPY_CODE"
                          ? "Copy Code"
                          : btnType === "PHONE_NUMBER"
                          ? "Call Us"
                          : btnType}
                      </span>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Template Configuration & Parameter Schema Details */}
          <Box
            padding={3}
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
              <Box display="flex" flexDirection="column">
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#1e293b" }}>
                  {template.displayName}
                </Typography>
                <Typography variant="caption" style={{ fontFamily: "monospace", color: "#64748b" }}>
                  Meta ID: {template.name}
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Chip size="small" label={template.category} />
                <Chip size="small" label={`Lang: ${template.languageCode}`} variant="outlined" />
              </Box>
            </Box>

            {/* Expected Parameters Schema */}
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="caption" style={{ fontWeight: 700, color: "#475569" }}>
                Required Campaign Broadcast Parameters ({Object.keys(schemaMap).length}):
              </Typography>
              {Object.keys(schemaMap).length > 0 ? (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {Object.entries(schemaMap).map(([key, label]) => (
                    <Box
                      key={key}
                      style={{
                        backgroundColor: "#f8fafc",
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                        padding: "4px 10px",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: "0.85rem",
                      }}
                    >
                      <strong style={{ color: "#2563eb" }}>{key}</strong>
                      <span style={{ color: "#64748b" }}>→</span>
                      <span style={{ color: "#334155" }}>{String(label)}</span>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Text size={1} color="default2">
                  No dynamic parameters required (Static template).
                </Text>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px" }}>
        <Button variant="secondary" onClick={onClose}>
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplatePreviewDialog;
